import { Component, OnInit } from '@angular/core';
import {ContactMethodResponse} from "../../models/Common/contactMethodResponse";
import {CurrencyResponse} from "../../models/Common/currencyResponse";
import {NgxSpinnerService} from "ngx-spinner";
import {OrganizationService} from "../../services/organization.service";
import {CommonService} from "../../services/common.service";
import {FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {wholeNumberValidator} from "../../components/validators/whole-number-validator";
import {ACCOUNT_LIMITS, ACCOUNT_TEXT_LIMITS} from "../../constants/dataverse-field-limits";
import {Router} from "@angular/router";
import {Organization} from "../../models/Organization/organizationResponse";
import swal from "sweetalert2";
import {CountryResponse, IndustryResponse} from 'src/app/models/Common/countryResponse';

@Component({
  selector: 'app-organization',
  templateUrl: './organization.component.html',
  styleUrls: ['./organization.component.scss']
})
export class OrganizationComponent implements OnInit{
  contactMethods: ContactMethodResponse[] = [];
  currency: CurrencyResponse[] = [];
  counties: CountryResponse[] = [];
  organization: Organization | undefined;
  industries: IndustryResponse[] = [];



  constructor(private spinner: NgxSpinnerService,
              private organizationService: OrganizationService, // Inject service
              private commonService: CommonService,
              private router: Router) {
  }


  ngOnInit() {

    this.spinner.show();
    Promise.all([
      this.loadContactMethods(),
      this.loadCurrency(),
      this.loadOrganization(),
      this.loadCountry(),
      this.loadIndustry()
    ]).finally(() => this.spinner.hide());

  }



  loadContactMethods() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getContactMethod().subscribe({
        next: (response) => {
          this.contactMethods = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading contact methods", error);
          reject(error);
        },
      });
    });
  }

  loadCurrency() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getCurrency().subscribe({
        next: (response) => {
          this.currency = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading currency", error);
          reject(error);
        },
      });
    });
  }

  loadCountry() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getCountryEntity().subscribe({
        next: (response) => {
          this.counties = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading counties", error);
          reject(error);
        },
      });
    });
  }

  loadIndustry() {
    return new Promise<void>((resolve, reject) => {
      this.commonService.getIndustriesEntity().subscribe({
        next: (response) => {
          this.industries = response.item || [];
          resolve();
        },
        error: (error) => {
          console.error("Error loading counties", error);
          reject(error);
        },
      });
    });
  }

  loadOrganization() {
    return new Promise<void>((resolve, reject) => {
      this.organizationService.getOrganizationInfo().subscribe({
        next: (response) => {
          this.organization = response.item;
          if (this.organization) {
            this.organizationForm.patchValue({
              organizeInfo: {
                id: this.organization.organizeInfo?.id || '',
                name: this.organization.organizeInfo?.name || '',
                addressStreet: this.organization.organizeInfo?.addressStreet || '',
                addressCity: this.organization.organizeInfo?.addressCity || '',
                addressPostal: this.organization.organizeInfo?.addressPostal || '',
                addressCountry: this.organization.organizeInfo?.addressCountry || '',
                webSiteUrl: this.organization.organizeInfo?.webSiteUrl || '',
                annualBudget: this.organization.organizeInfo?.annualBudget || '',
                phone: this.organization.organizeInfo?.phone || '',
                numberOfEmployees: this.organization.organizeInfo?.numberOfEmployees || '',
                description: this.organization.organizeInfo?.description || '',
                numberOfVoulenteers: this.organization.organizeInfo?.numberOfVoulenteers || '',
                industry:this.organization.organizeInfo?.industry,
                currencyId:this.organization.organizeInfo?.currencyId,
                references: this.organization.organizeInfo?.references || '',
                email: this.organization.organizeInfo?.email || '',
              }
            });
          }
          resolve();
        },
        error: (error) => {
          console.error("Error loading organization", error);
          reject(error);
        },
      });
    });
  }

  /** account column ranges - see src/app/constants/dataverse-field-limits.ts */
  protected readonly accountLimits = ACCOUNT_LIMITS;
  /** account text column limits. This page posts JSON, so plain maxLength is exact here. */
  protected readonly accountTextLimits = ACCOUNT_TEXT_LIMITS;

  organizationForm: FormGroup = new FormGroup({
    organizeInfo: new FormGroup({
      id: new FormControl(''),
      name: new FormControl({value: '', disabled: true}),
      addressStreet: new FormControl('', [Validators.required, Validators.maxLength(ACCOUNT_TEXT_LIMITS.addressStreet)]),
      addressCity: new FormControl('', [Validators.required, Validators.maxLength(ACCOUNT_TEXT_LIMITS.addressCity)]),
      addressPostal: new FormControl('', [Validators.required, Validators.maxLength(ACCOUNT_TEXT_LIMITS.addressPostal)]),
      addressCountry: new FormControl('', Validators.required),
      webSiteUrl: new FormControl('', [Validators.required, Validators.maxLength(ACCOUNT_TEXT_LIMITS.webSiteUrl)]),
      annualBudget: new FormControl('', [Validators.required, wholeNumberValidator(),
        Validators.min(ACCOUNT_LIMITS.annualBudget.min), Validators.max(ACCOUNT_LIMITS.annualBudget.max)]),
      currencyId: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required, Validators.maxLength(ACCOUNT_TEXT_LIMITS.phone)]),
      numberOfEmployees: new FormControl('', [Validators.required, wholeNumberValidator(),
        Validators.min(ACCOUNT_LIMITS.numberOfEmployees.min), Validators.max(ACCOUNT_LIMITS.numberOfEmployees.max)]),
      description: new FormControl('', [Validators.required, Validators.maxLength(ACCOUNT_TEXT_LIMITS.description)]),
      numberOfVoulenteers: new FormControl('', [Validators.required, wholeNumberValidator(),
        Validators.min(ACCOUNT_LIMITS.numberOfVoulenteers.min), Validators.max(ACCOUNT_LIMITS.numberOfVoulenteers.max)]),
      industry: new FormControl('', Validators.required),
      references: new FormControl('', [Validators.required, Validators.maxLength(ACCOUNT_TEXT_LIMITS.references)]),
      email: new FormControl({value: '', disabled: true}),
    }),
  });


  onContractClick() {
    if (this.organization?.organizeInfo.id) {
      this.router.navigate(['/contact'], { queryParams: { organizationId: this.organization?.organizeInfo.id } });
    }
  }

  async addPaymentInfo() {
    if (this.organization?.organizeInfo.id) {
      await this.router.navigate(['/payment-info'], {queryParams: {organizationId: this.organization?.organizeInfo.id}});
    }
  }




  onSubmit() {
    this.organizationForm.markAllAsTouched();
    if (this.organizationForm.invalid) {

      const findInvalidControls = (form: FormGroup | FormArray, path: string = ''): string[] => {
        const invalidControls: string[] = [];

        // Loop through the keys in the current form group or form array
        Object.keys(form.controls).forEach(key => {
          const control = form.get(key);
          const controlPath = path ? `${path}.${key}` : key;

          // If the control is another FormGroup or FormArray, call the function recursively
          if (control instanceof FormGroup || control instanceof FormArray) {
            invalidControls.push(...findInvalidControls(control, controlPath));
          }
          // If the control is invalid, add its path to the list
          else if (control && control.invalid) {
            invalidControls.push(controlPath);
          }
        });

        return invalidControls;
      };

      // Pass the root form group to the function
      const invalidControls = findInvalidControls(this.organizationForm);

      console.warn('Invalid controls:', invalidControls);


      swal.fire({
        title: 'Please fill in all required fields.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    } else {
      const formData = this.organizationForm.value;
      this.spinner.show();

      this.organizationService.update(formData).subscribe({
        next: (_) => {
          this.spinner.hide();
          if (_.result){
            swal.fire({
              title: 'The organization has been updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
            });
          }
          else {
            swal.fire({
              title: _.messages?.join(','),
              icon: 'error',
              confirmButtonText: 'OK',
            });
          }
        },
        error: (_) => {
          this.spinner.hide();
          // Reported by HttpErrorInterceptor, which has the readable text and the reference id.
        }
      });
    }

  }

}


